import React, { useState, useEffect, useRef } from 'react';

/**
 * VideoTutorialModal - Modal for selecting and playing tutorial videos
 * Shows two options: Guardian tutorial and Kid tutorial
 */
const VideoTutorialModal = ({ isOpen, onClose, t }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const modalRef = useRef(null);
  const videoRef = useRef(null);

  const videos = [
    {
      id: 'guardian',
      path: '/video/parent.mp4',
      labelKey: 'dashboard.index.video_tutorials.guardian_tutorial',
      defaultLabel: 'Hướng Dẫn Cho Phụ Huynh',
      icon: 'bi-person-badge',
    },
    {
      id: 'kid',
      path: '/video/kid.mp4',
      labelKey: 'dashboard.index.video_tutorials.kid_tutorial',
      defaultLabel: 'Hướng Dẫn Cho Trẻ',
      icon: 'bi-emoji-smile',
    },
  ];

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedVideo(null);
      setIsPlaying(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setSelectedVideo(null);
    setIsPlaying(false);
    onClose();
  };

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    setIsPlaying(true);
  };

  const handleBackToSelection = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setSelectedVideo(null);
    setIsPlaying(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const modalTitle = t('dashboard.index.video_tutorials.title') || 'Video Hướng Dẫn';
  const selectPrompt = t('dashboard.index.video_tutorials.select_prompt') || 'Chọn video hướng dẫn:';
  const backButton = t('common.back') || 'Quay lại';

  return (
    <div
      ref={modalRef}
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleBackdropClick}
      data-testid="video-tutorial-modal"
    >
      <div className={`modal-dialog modal-dialog-centered ${isPlaying ? 'modal-xl' : 'modal-md'}`}>
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold" data-testid="video-modal-title">
              {selectedVideo
                ? (t(selectedVideo.labelKey) || selectedVideo.defaultLabel)
                : modalTitle}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
              data-testid="video-modal-close"
            />
          </div>
          <div className="modal-body pt-2">
            {!selectedVideo ? (
              // Video selection view
              <div data-testid="video-selection-view">
                <p className="text-secondary mb-4">{selectPrompt}</p>
                <div className="d-flex flex-column gap-3">
                  {videos.map((video) => (
                    <button
                      key={video.id}
                      type="button"
                      className="btn btn-outline-primary btn-lg d-flex align-items-center justify-content-center gap-2 py-3"
                      onClick={() => handleVideoSelect(video)}
                      data-testid={`video-option-${video.id}`}
                    >
                      <i className={`bi ${video.icon} fs-4`}></i>
                      <span>{t(video.labelKey) || video.defaultLabel}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              // Video player view
              <div data-testid="video-player-view">
                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0"
                    onClick={handleBackToSelection}
                    data-testid="video-back-button"
                  >
                    <i className="bi bi-arrow-left me-1"></i>
                    {backButton}
                  </button>
                </div>
                <div className="ratio ratio-16x9">
                  <video
                    ref={videoRef}
                    src={selectedVideo.path}
                    controls
                    autoPlay
                    className="rounded-3"
                    data-testid="video-player"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTutorialModal;
